import { useState } from 'react'
import { Check, X, ChevronDown, ChevronUp } from 'lucide-react'
import Section, { SectionHeader } from '../components/Section'
import Button from '../components/Button'
import Card from '../components/Card'

const plans = [
    {
        name: 'Pro',
        badge: 'Most Popular',
        monthlyPrice: 29,
        yearlyPrice: 19,
        tagline: 'For individuals and solopreneurs',
        features: [
            'Unlimited voice capture',
            'Agentic auto-scheduler',
            'Chief of Staff memory (RAG)',
            'Universal inbox integration',
            'All calendar integrations',
            'Mobile, desktop, and web apps',
            'Priority support',
        ],
        note: 'Less than 1 hour of a virtual assistant',
    },
    {
        name: 'Team',
        badge: 'For Teams',
        monthlyPrice: 49,
        yearlyPrice: 39,
        tagline: 'For teams and organizations',
        features: [
            'Everything in Pro, plus:',
            'Shared project spaces',
            'Team resource planning',
            'Admin controls & permissions',
            'Advanced delegation tools',
            'Team analytics dashboard',
            'Dedicated success manager',
            'Custom integration support',
        ],
        note: 'Minimum 3 users',
    },
]

const comparisonData = [
    {
        category: 'Voice & Capture', features: [
            { name: 'Voice capture minutes', pro: 'Unlimited', team: 'Unlimited', enterprise: 'Unlimited' },
            { name: 'Multi-modal input', pro: true, team: true, enterprise: true },
            { name: 'Email forwarding', pro: true, team: true, enterprise: true },
        ]
    },
    {
        category: 'Intelligence & Automation', features: [
            { name: 'Auto-scheduler', pro: true, team: true, enterprise: true },
            { name: 'Chief of Staff memory', pro: true, team: true, enterprise: true },
            { name: 'Custom AI agents', pro: '3', team: '10', enterprise: 'Unlimited' },
        ]
    },
    {
        category: 'Collaboration', features: [
            { name: 'Personal workspace', pro: true, team: true, enterprise: true },
            { name: 'Shared projects', pro: false, team: true, enterprise: true },
            { name: 'Admin controls', pro: false, team: true, enterprise: true },
        ]
    },
]

const faqs = [
    {
        question: 'What happens after my 14-day trial?',
        answer: "Your trial includes full access to all Pro features. After 14 days, you'll be downgraded to a limited free tier (text-only capture, no auto-scheduling). Upgrade anytime to restore full functionality.",
    },
    {
        question: 'Can I cancel anytime?',
        answer: 'Yes, absolutely. Cancel with one click from your account settings. No questions asked.',
    },
    {
        question: 'How is Way2Will different from Motion or Sunsama?',
        answer: "Motion uses rigid algorithms; Sunsama requires manual drag-and-drop. Way2Will combines voice-first capture with flexible AI that negotiates with you, not at you.",
    },
    {
        question: 'Is my voice data private and secure?',
        answer: "Yes. We're GDPR and BIPA compliant. Your voice data is encrypted, never sold, and never used to train third-party models. You can delete all data with one click.",
    },
    {
        question: 'Do you offer student or nonprofit discounts?',
        answer: 'Yes! Students get 50% off Pro. Nonprofits get 30% off. Contact support with verification.',
    },
]

export default function Pricing() {
    const [isYearly, setIsYearly] = useState(true)
    const [openFaq, setOpenFaq] = useState<number | null>(null)

    return (
        <>
            {/* Hero Section */}
            <Section className="pt-12 lg:pt-20">
                <div className="text-center max-w-4xl mx-auto">
                    <h1 className="text-4xl lg:text-5xl font-bold text-primary-color leading-tight mb-6">
                        Pricing That Makes Sense
                    </h1>
                    <p className="text-lg lg:text-xl font-normal text-secondary-color leading-relaxed mb-8">
                        Try everything free for 14 days. No credit card required.
                    </p>
                    <div className="inline-flex items-center gap-2 glass px-4 py-2 rounded-full">
                        <span className="text-base">🎁</span>
                        <span className="text-sm text-secondary-color">Full access during trial • Cancel anytime • No hidden fees</span>
                    </div>
                </div>
            </Section>

            {/* Pricing Tiers */}
            <Section glass>
                {/* Billing Toggle */}
                <div className="flex justify-center items-center gap-4 mb-12">
                    <span className={`text-base ${!isYearly ? 'text-primary-color font-semibold' : 'text-secondary-color'}`}>
                        Monthly
                    </span>
                    <button
                        onClick={() => setIsYearly(!isYearly)}
                        className="relative w-14 h-7 rounded-full transition-colors"
                        style={{ backgroundColor: isYearly ? '#5B9BD5' : 'rgba(91, 155, 213, 0.3)' }}
                    >
                        <div
                            className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${isYearly ? 'left-7' : 'left-0.5'
                                }`}
                        />
                    </button>
                    <span className={`text-base ${isYearly ? 'text-primary-color font-semibold' : 'text-secondary-color'}`}>
                        Annual
                    </span>
                    {isYearly && (
                        <span className="text-xs font-semibold text-white bg-success px-2 py-1 rounded-full">
                            Save 34%
                        </span>
                    )}
                </div>

                {/* Pricing Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                    {plans.map((plan) => (
                        <Card key={plan.name} variant="elevated" className="flex flex-col p-8">
                            <div className="flex items-center gap-2 mb-4">
                                <span className="text-xs font-semibold text-white bg-primary-base px-2 py-1 rounded-full">
                                    {plan.badge}
                                </span>
                            </div>
                            <h3 className="text-2xl font-semibold text-primary-color mb-2">{plan.name}</h3>
                            <div className="flex items-baseline gap-1 mb-2">
                                <span className="text-4xl font-bold text-primary-color">
                                    ${isYearly ? plan.yearlyPrice : plan.monthlyPrice}
                                </span>
                                <span className="text-secondary-color">/month</span>
                                {plan.name === 'Team' && <span className="text-secondary-color">/user</span>}
                            </div>
                            {isYearly && (
                                <p className="text-sm text-tertiary-color mb-4">billed annually</p>
                            )}
                            <p className="text-secondary-color mb-6">{plan.tagline}</p>

                            <Button to="/signup" className="w-full mb-6">
                                Start 14-Day Free Trial
                            </Button>

                            <ul className="flex flex-col gap-3 flex-1">
                                {plan.features.map((feature, index) => (
                                    <li key={index} className="flex items-start gap-3">
                                        <Check className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                                        <span className="text-sm text-secondary-color">{feature}</span>
                                    </li>
                                ))}
                            </ul>

                            <p className="text-xs text-tertiary-color mt-6 pt-4 border-t border-primary-base/10">
                                {plan.note}
                            </p>
                        </Card>
                    ))}
                </div>

                {/* Enterprise */}
                <div className="max-w-4xl mx-auto mt-8">
                    <Card variant="default" className="flex flex-col md:flex-row items-center justify-between p-8 gap-6">
                        <div>
                            <h3 className="text-xl font-semibold text-primary-color mb-2">Enterprise</h3>
                            <p className="text-secondary-color mb-4">Custom solutions for large organizations</p>
                            <div className="flex flex-wrap gap-4">
                                <span className="text-sm text-tertiary-color">SSO & advanced security</span>
                                <span className="text-sm text-tertiary-color">•</span>
                                <span className="text-sm text-tertiary-color">Custom data retention</span>
                                <span className="text-sm text-tertiary-color">•</span>
                                <span className="text-sm text-tertiary-color">Volume pricing</span>
                            </div>
                        </div>
                        <Button variant="secondary">Contact Sales</Button>
                    </Card>
                </div>
            </Section>

            {/* Feature Comparison Table */}
            <Section>
                <SectionHeader title="Compare Plans" />
                <div className="overflow-x-auto">
                    <table className="w-full glass rounded-2xl overflow-hidden">
                        <thead>
                            <tr className="border-b border-primary-base/10">
                                <th className="p-4 text-left text-primary-color font-semibold">Feature</th>
                                <th className="p-4 text-center text-primary-base font-semibold">Pro</th>
                                <th className="p-4 text-center text-primary-base font-semibold">Team</th>
                                <th className="p-4 text-center text-primary-base font-semibold">Enterprise</th>
                            </tr>
                        </thead>
                        <tbody>
                            {comparisonData.map((category) => (
                                <>
                                    <tr key={category.category} className="bg-primary-base/5">
                                        <td colSpan={4} className="p-3 text-sm font-semibold text-primary-color">
                                            {category.category}
                                        </td>
                                    </tr>
                                    {category.features.map((feature) => (
                                        <tr key={feature.name} className="border-b border-primary-base/5">
                                            <td className="p-4 text-secondary-color">{feature.name}</td>
                                            <td className="p-4 text-center">
                                                {typeof feature.pro === 'boolean' ? (
                                                    feature.pro ? <Check className="w-5 h-5 text-success mx-auto" /> : <X className="w-5 h-5 text-error mx-auto" />
                                                ) : (
                                                    <span className="text-secondary-color">{feature.pro}</span>
                                                )}
                                            </td>
                                            <td className="p-4 text-center">
                                                {typeof feature.team === 'boolean' ? (
                                                    feature.team ? <Check className="w-5 h-5 text-success mx-auto" /> : <X className="w-5 h-5 text-error mx-auto" />
                                                ) : (
                                                    <span className="text-secondary-color">{feature.team}</span>
                                                )}
                                            </td>
                                            <td className="p-4 text-center">
                                                {typeof feature.enterprise === 'boolean' ? (
                                                    feature.enterprise ? <Check className="w-5 h-5 text-success mx-auto" /> : <X className="w-5 h-5 text-error mx-auto" />
                                                ) : (
                                                    <span className="text-secondary-color">{feature.enterprise}</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Section>

            {/* FAQ Section */}
            <Section glass>
                <SectionHeader title="Frequently Asked Questions" />
                <div className="max-w-3xl mx-auto flex flex-col gap-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="glass rounded-xl overflow-hidden">
                            <button
                                onClick={() => setOpenFaq(openFaq === index ? null : index)}
                                className="w-full flex items-center justify-between p-6 text-left"
                            >
                                <span className="text-base font-semibold text-primary-color">{faq.question}</span>
                                {openFaq === index ? (
                                    <ChevronUp className="w-5 h-5 text-primary-base" />
                                ) : (
                                    <ChevronDown className="w-5 h-5 text-primary-base" />
                                )}
                            </button>
                            {openFaq === index && (
                                <div className="px-6 pb-6">
                                    <p className="text-secondary-color">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </Section>

            {/* CTA Section */}
            <Section>
                <div className="max-w-4xl mx-auto text-center">
                    <div className="glass-prominent p-12 lg:p-16 rounded-2xl">
                        <h2 className="text-3xl lg:text-4xl font-semibold text-primary-color mb-6">
                            Start Your Journey to Antigravity Productivity
                        </h2>
                        <p className="text-lg text-secondary-color mb-8">
                            Join thousands of professionals experiencing effortless organization.
                        </p>
                        <Button to="/signup" size="lg">
                            Start 14-Day Free Trial
                        </Button>
                        <p className="text-sm text-tertiary-color mt-6">
                            No credit card required • Full access • Cancel anytime
                        </p>
                        <div className="mt-4 inline-flex items-center gap-2 glass px-4 py-2 rounded-full">
                            <span className="text-success">✓</span>
                            <span className="text-sm text-secondary-color">30-day money-back guarantee</span>
                        </div>
                    </div>
                </div>
            </Section>
        </>
    )
}
